"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDonate } from "@/lib/api/billing";
import { HeartIcon } from "lucide-react";
const DEFAULT_AMOUNT = "5";
const PRESETS = ["3", "5", "10", "25"];
const MIN_EUROS = 1;
const MAX_EUROS = 1000;

/**
 * A public donation button + dialog. Anyone (including logged-out visitors) can
 * enter a custom amount (defaulting to €5) and be sent to the Mollie checkout.
 */
export function DonateDialog(t0) {
  const $ = _c(62);
  const {
    triggerClassName
  } = t0;
  const t = useTranslations("donation");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const donate = useDonate();
  let t1;
  let t2;
  if ($[0] !== t) {
    t1 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      if (params.get("donation") === "complete") {
        toast.success(t("success"));
        params.delete("donation");
        const query = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : ""));
      }
    };
    t2 = [t];
    $[0] = t;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== amount) {
    t3 = amount.replace(",", ".");
    $[3] = amount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const euros = Number(t3);
  let t4;
  if ($[5] !== euros) {
    t4 = Number.isFinite(euros) && euros >= MIN_EUROS && euros <= MAX_EUROS;
    $[5] = euros;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isValid = t4;
  let t5;
  if ($[7] !== donate || $[8] !== euros || $[9] !== isValid || $[10] !== t) {
    t5 = function handleDonate() {
      if (!isValid) {
        toast.error(t("invalid", {
          min: MIN_EUROS,
          max: MAX_EUROS
        }));
        return;
      }
      const amountCents = Math.round(euros * 100);
      donate.mutate(amountCents, {
        onSuccess: _temp,
        onError: () => toast.error(t("error"))
      });
    };
    $[7] = donate;
    $[8] = euros;
    $[9] = isValid;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleDonate = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <HeartIcon className="h-4 w-4" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("cta");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7 || $[16] !== triggerClassName) {
    t8 = <DialogTrigger asChild={true}><Button variant="outline" size="sm" className={triggerClassName}>{t6}{t7}</Button></DialogTrigger>;
    $[15] = t7;
    $[16] = triggerClassName;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("title");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <DialogTitle>{t9}</DialogTitle>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("description");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <DialogDescription>{t11}</DialogDescription>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <DialogHeader>{t10}{t12}</DialogHeader>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== amount) {
    t14 = PRESETS.map(preset => <Button key={preset} type="button" variant={amount === preset ? "default" : "outline"} size="sm" onClick={() => setAmount(preset)}>{preset} €</Button>);
    $[29] = amount;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <div className="flex flex-wrap gap-2">{t14}</div>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("amount_label");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16) {
    t17 = <Label htmlFor="donation-amount">{t16}</Label>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = e => setAmount(e.target.value);
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = !isValid;
  let t20;
  if ($[38] !== amount || $[39] !== t19) {
    t20 = <Input id="donation-amount" type="number" min={MIN_EUROS} max={MAX_EUROS} step="1" inputMode="decimal" value={amount} onChange={t18} aria-invalid={t19} />;
    $[38] = amount;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t17 || $[42] !== t20) {
    t21 = <div className="grid gap-2">{t17}{t20}</div>;
    $[41] = t17;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t15 || $[45] !== t21) {
    t22 = <div className="grid gap-4">{t15}{t21}</div>;
    $[44] = t15;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const t23 = !isValid || donate.isPending;
  let t24;
  if ($[47] !== donate.isPending || $[48] !== t) {
    t24 = donate.isPending ? t("pending") : t("submit");
    $[47] = donate.isPending;
    $[48] = t;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== handleDonate || $[51] !== t23 || $[52] !== t24) {
    t25 = <DialogFooter><Button onClick={handleDonate} disabled={t23}>{t24}</Button></DialogFooter>;
    $[50] = handleDonate;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t13 || $[55] !== t22 || $[56] !== t25) {
    t26 = <DialogContent>{t13}{t22}{t25}</DialogContent>;
    $[54] = t13;
    $[55] = t22;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== open || $[59] !== t26 || $[60] !== t8) {
    t27 = <Dialog open={open} onOpenChange={setOpen}>{t8}{t26}</Dialog>;
    $[58] = open;
    $[59] = t26;
    $[60] = t8;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}
function _temp(url) {
  if (url) {
    window.location.href = url;
  }
}